import { useState, useMemo } from 'react';

const ZONE_COLORS = {
    attractive: '#22c55e',
    attractiveFill: 'rgba(22, 101, 52, 0.6)',
    fair: '#eab308',
    high: '#ef4444',
};

function formatPrice(value) {
    if (value == null) return '-';
    return Math.round(value).toLocaleString('vi-VN');
}

function formatPercent(value) {
    if (value == null) return '-';
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
}

function ValuationGauge({ currentPrice, intrinsicValue, safetyMargin }) {
    const fairLow = intrinsicValue * 0.8;
    const fairHigh = intrinsicValue * 1.2;
    const maxRange = Math.max(fairHigh, currentPrice * 1.2, intrinsicValue * 1.2);

    const attractiveEndPct = (fairLow / maxRange) * 100;
    const fairEndPct = (fairHigh / maxRange) * 100;
    const currentPricePct = Math.min(100, Math.max(0, (currentPrice / maxRange) * 100));
    const intrinsicPct = Math.min(100, Math.max(0, (intrinsicValue / maxRange) * 100));

    return (
        <div className="relative pb-4">
            <div
                className="absolute -top-10 text-center"
                style={{ left: `${(currentPricePct + intrinsicPct) / 2}%`, transform: 'translateX(-50%)' }}
            >
                <div className={`text-sm font-semibold ${safetyMargin >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {formatPercent(safetyMargin)}
                </div>
                <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">Biên an toàn</div>
                <div
                    className="absolute left-1/2 w-px"
                    style={{ top: '100%', height: '52px', transform: 'translateX(-50%)', backgroundColor: '#ffffff', zIndex: 10 }}
                />
            </div>

            <div className="relative mt-12">
                <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 10 }}>
                    <div className="absolute" style={{ left: `${currentPricePct}%`, transform: 'translateX(-50%)', height: '32px', top: '-8px', width: '2px', backgroundColor: 'rgba(156, 163, 175, 0.3)' }} />
                    <div className="absolute" style={{ left: `${intrinsicPct}%`, transform: 'translateX(-50%)', height: '96px', top: '-8px', width: '2px', backgroundColor: 'rgba(156, 163, 175, 0.3)' }} />
                    <div className="absolute" style={{
                        left: `${Math.min(currentPricePct, intrinsicPct)}%`,
                        width: `${Math.abs(currentPricePct - intrinsicPct)}%`,
                        height: '2px', top: '-8px',
                        backgroundColor: safetyMargin >= 0 ? 'rgba(22, 163, 74, 0.5)' : 'rgba(239, 68, 68, 0.5)',
                    }} />
                </div>

                <div className="relative h-28 rounded-lg overflow-visible">
                    <div className="absolute inset-0 flex rounded-lg overflow-hidden">
                        <div style={{ width: `${attractiveEndPct}%`, backgroundColor: ZONE_COLORS.attractive }} />
                        <div style={{ width: `${fairEndPct - attractiveEndPct}%`, backgroundColor: ZONE_COLORS.fair }} />
                        <div style={{ width: `${100 - fairEndPct}%`, backgroundColor: ZONE_COLORS.high }} />
                    </div>

                    <div className="absolute left-0 rounded-r" style={{ width: `${currentPricePct}%`, top: '8px', height: 'calc(35% - 8px)' }}>
                        <div className="absolute inset-0 rounded-r" style={{ backgroundColor: ZONE_COLORS.attractiveFill }} />
                        <div className="relative h-full flex items-center justify-end pr-3">
                            <div className="text-right">
                                <div className="text-[9px] text-white whitespace-nowrap leading-tight">Giá cổ phiếu hiện tại</div>
                                <div className="text-xs font-bold text-white">{formatPrice(currentPrice)}</div>
                            </div>
                        </div>
                    </div>

                    <div className="absolute left-0 rounded-r" style={{ width: `${intrinsicPct}%`, bottom: '8px', height: 'calc(35% - 8px)' }}>
                        <div className="absolute inset-0 rounded-r" style={{ backgroundColor: ZONE_COLORS.attractiveFill }} />
                        <div className="relative h-full flex items-center justify-end pr-3">
                            <div className="text-right">
                                <div className="text-[9px] text-white whitespace-nowrap leading-tight">Mức định giá</div>
                                <div className="text-xs font-bold text-white">{formatPrice(intrinsicValue)}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function ValuationChart({ ticker, company_name, current_price, intrinsic_value, methods, currency = 'vnđ' }) {
    const [isMethodsExpanded, setIsMethodsExpanded] = useState(false);

    const computed = useMemo(() => {
        if (!current_price || !intrinsic_value) return null;
        const upside = ((intrinsic_value - current_price) / current_price) * 100;
        const safetyMargin = ((intrinsic_value - current_price) / intrinsic_value) * 100;
        return { upside, safetyMargin, isPositive: upside >= 0 };
    }, [current_price, intrinsic_value]);

    if (!computed) return null;

    return (
        <div>
            <div className="py-3 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center justify-between">
                    <div>
                        <div className={`text-sm font-medium ${computed.isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {computed.isPositive ? '↑' : '↓'} ({formatPercent(Math.abs(computed.upside)).replace('+', '')} {computed.isPositive ? 'Upside' : 'Downside'})
                        </div>
                        <div className="flex items-baseline gap-2">
                            <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">{formatPrice(intrinsic_value)}</span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">{currency}/cổ phiếu</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Giá trị nội tại</div>
                    </div>
                    <div className="text-right">
                        <div className="text-sm font-medium text-gray-700 dark:text-gray-300">{ticker}</div>
                        {company_name && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 max-w-[180px] truncate">{company_name}</div>
                        )}
                    </div>
                </div>
            </div>

            <div className="py-1">
                <ValuationGauge
                    currentPrice={current_price}
                    intrinsicValue={intrinsic_value}
                    safetyMargin={computed.safetyMargin}
                />
            </div>

            <div className="pb-4 flex flex-wrap items-center justify-center gap-4 text-xs">
                <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded" style={{ backgroundColor: ZONE_COLORS.attractive }} />
                    <span className="text-gray-600 dark:text-gray-400">Vùng giá hấp dẫn</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded" style={{ backgroundColor: ZONE_COLORS.fair }} />
                    <span className="text-gray-600 dark:text-gray-400">Mức định giá ± 20%</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded" style={{ backgroundColor: ZONE_COLORS.high }} />
                    <span className="text-gray-600 dark:text-gray-400">Vùng giá cao</span>
                </div>
            </div>

            {methods && methods.length > 0 && (
                <div className="pb-4">
                    <button
                        onClick={() => setIsMethodsExpanded(!isMethodsExpanded)}
                        className="flex items-center gap-2 w-full text-left py-2"
                    >
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Các phương pháp định giá</span>
                        <svg
                            className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform ${isMethodsExpanded ? 'rotate-180' : ''}`}
                            fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                    {isMethodsExpanded && (
                        <div className="overflow-x-auto mt-3">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-gray-200 dark:border-gray-700">
                                        <th className="text-left py-2 px-2 text-gray-600 dark:text-gray-400 font-medium">Chỉ số</th>
                                        <th className="text-right py-2 px-2 text-gray-600 dark:text-gray-400 font-medium">Giá trị ({currency.toUpperCase()})</th>
                                        {methods.some(m => m.weight != null) && (
                                            <th className="text-right py-2 px-2 text-gray-600 dark:text-gray-400 font-medium">Tỷ trọng</th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {methods.map((m, i) => (
                                        <tr key={i} className="border-b border-gray-100 dark:border-gray-800">
                                            <td className="py-2 px-2 text-gray-700 dark:text-gray-300">{m.name}</td>
                                            <td className="py-2 px-2 text-right text-gray-900 dark:text-gray-100">{formatPrice(m.value)}</td>
                                            {m.weight != null && (
                                                <td className="py-2 px-2 text-right text-gray-900 dark:text-gray-100">{m.weight}%</td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            <div className="pb-4">
                <div className="flex items-start gap-2">
                    <span className="text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5">⚠</span>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                        <span className="font-semibold">Miễn trừ trách nhiệm: </span>
                        Số liệu về Giá trị nội tại chỉ dành cho mục đích tham khảo và không phải là yếu tố duy nhất quyết định đến giá cổ phiếu.
                    </p>
                </div>
            </div>

        </div>
    );
}
